import { z } from 'zod';

import { validateName } from '../nameUtils';

/**
 * Validation utilities for consistent front-end validation across the application.
 * These utilities should be used for all name, address, SSN, and related input validations.
 */

// ==================== NAME VALIDATION ====================

/**
 * Builds a Zod refinement that delegates to validateName; on throw, the error message
 * is surfaced as the field error.
 */
const refineWithValidateName =
    (isLegal: boolean) => (val: string, ctx: z.RefinementCtx) => {
        try {
            validateName(val, isLegal);
        } catch (err) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err instanceof Error ? err.message : 'Invalid name',
            });
        }
    };

/**
 * Zod schema for validating first names.
 * Delegates to validateName (display name rules): required, allowed characters only, min 2 chars.
 *
 * Use this for all first name inputs across the application.
 */
export const zodFirstNameValidation = z
    .string()
    .superRefine(refineWithValidateName(false));

/**
 * Zod schema for validating last names.
 * Delegates to validateName (display name rules): required, allowed characters only, min 2 chars.
 *
 * Use this for all last name inputs across the application.
 */
export const zodLastNameValidation = z
    .string()
    .superRefine(refineWithValidateName(false));

/**
 * Zod schema for validating legal first names (used for KYC, Synctera, Apex).
 * Delegates to validateName with isLegal: true (stricter length and character rules).
 *
 * Use this for legal name inputs that will be sent to banking partners.
 */
export const zodLegalFirstNameValidation = z
    .string()
    .superRefine(refineWithValidateName(true));

/**
 * Zod schema for validating legal last names (used for KYC, Synctera, Apex).
 * Delegates to validateName with isLegal: true (stricter length and character rules).
 *
 * Use this for legal name inputs that will be sent to banking partners.
 */
export const zodLegalLastNameValidation = z
    .string()
    .superRefine(refineWithValidateName(true));
